/* The platform formatting vocabulary — one semantic per concept, shared by
   every console. Data renders in mono + tabular-nums; these only produce the
   strings ("—" for absent values).

   Decided semantics (do not fork per app):
   - sizes are binary (1024) and labeled B…PB;
   - instants, days and typed times are the clock's (lib/clock.ts): one
     Local/UTC switch, and `stamp` is the one way an instant becomes text;
   - durations are seconds → "X.Ys" / "Xm Ys" / "Xh Ym". */

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let value = bytes;
  let unit = "B";
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${unit}`;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** True for a value that identifies a record to a machine and means nothing to a person:
 *  a UUID, a digest (bare, `sha256:`-prefixed, or under a dotted or underscored prefix such
 *  as `corpora.24ef…` or `ftjob_84fd…`). Such a value is never shown as a name (BRAND §13). */
export function isOpaqueId(value: string): boolean {
  const text = value.trim();
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      text,
    ) ||
    /^(?:[a-z0-9]+:)?[0-9a-f]{12,}$/i.test(text) ||
    /^[0-9A-HJKMNP-TV-Z]{26}$/.test(text) ||
    /^[a-z][a-z0-9-]*[._:][0-9a-f]{8,}$/i.test(text)
  );
}

/** A machine token as words, or `fallback` when it is only an identifier. The id test
 *  reads the raw value: humanizing first would break an id's shape and let it through. */
export function readableOr(
  value: string | null | undefined,
  fallback: string,
): string {
  const text = value?.trim() ?? "";
  return text === "" || isOpaqueId(text) ? fallback : humanizeToken(text);
}

/** A record's name, or `fallback` when the name it carries is only an identifier. */
export function nameOr(
  value: string | null | undefined,
  fallback: string,
): string {
  const text = value?.trim() ?? "";
  return text === "" || isOpaqueId(text) ? fallback : text;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}

export function formatRunDuration(
  started: string,
  finished: string | null,
): string {
  const end = finished ? new Date(finished).getTime() : Date.now();
  const seconds = Math.max(0, (end - new Date(started).getTime()) / 1000);
  return formatDuration(seconds);
}

/** A machine token as words: `semantic_index` → "Semantic index", `human-teleop` →
 *  "Human teleop". Mechanical on purpose — it reads any closed vocabulary without a second
 *  hand-kept list of its values. */
export function humanizeToken(token: string): string {
  const words = token
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[_\-\s./]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
  if (words.length === 0) return token;
  const sentence = words.join(" ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

const HASH_LIKE = /^(?=[0-9a-f]*\d)[0-9a-f]{8,}$/i;

/** The human parts of a machine name, outermost first: hash fragments, `key=` prefixes and
 *  path noise go, so `segment_id=distractor-e42c05c81ac1/episode-00` reads
 *  ["distractor", "episode 00"] — a cell shows the first as the value and the rest as its
 *  subtext. Empty when nothing human is left. */
export function readableParts(value: string): string[] {
  return value
    .split(",")[0]!
    .replace(/^[\w.]+=/, "")
    .replace(/^"|"$/g, "")
    .split("/")
    .filter((part) => !isOpaqueId(part))
    .map((part) =>
      part
        .split(/[-_.]/)
        .filter((word) => word && !HASH_LIKE.test(word))
        .join(" "),
    )
    .filter(Boolean);
}
